import { assert, clamp, Listeners, Notifier, Option, panic, Terminator } from "@opendaw/lib-std";
import { Pointers } from "@opendaw/studio-enums";
import { TrackBoxAdapter } from "./timeline/TrackBoxAdapter";
const ExternalControlTypes = [
    Pointers.Automation,
    Pointers.Modulation,
    Pointers.MidiControl,
    Pointers.ParameterController
];
export class AutomatableParameterFieldAdapter {
    #context;
    #field;
    #valueMapping;
    #stringMapping;
    #name;
    #anchor;
    #terminator = new Terminator();
    #valueChangeNotifier;
    #controlSource;
    #trackBoxAdapter = Option.None;
    #automationHandle = Option.None;
    #controlledValue = null;
    #midiControlled = false;
    constructor(context, field, valueMapping, stringMapping, name, anchor) {
        this.#context = context;
        this.#field = field;
        this.#valueMapping = valueMapping;
        this.#stringMapping = stringMapping;
        this.#name = name;
        this.#anchor = anchor ?? 0.0;
        this.#terminator.own(this.#context.parameterFieldAdapters.register(this));
        this.#valueChangeNotifier = this.#terminator.own(new Notifier());
        this.#controlSource = new Listeners();
        this.#terminator.own(this.#field.subscribe(() => this.#valueChangeNotifier.notify(this)));
        this.#terminator.own(this.#field.pointerHub.catchupAndSubscribe({
            onAdded: (pointer) => {
                this.#controlSource.proxy.onControlSourceAdd(mapPointerToControlSource(pointer.pointerType));
                pointer.box.accept({
                    visitTrackBox: (box) => {
                        assert(this.#trackBoxAdapter.isEmpty(), "Already assigned");
                        const adapter = this.#context.boxAdapters.adapterFor(box, TrackBoxAdapter);
                        this.#trackBoxAdapter = Option.wrap(adapter);
                        if (this.#context.isMainThread) {
                            this.#automationHandle = Option.wrap(this.#context.liveStreamReceiver
                                .subscribeFloat(this.#field.address, value => {
                                if (this.#controlledValue === value) {
                                    return;
                                }
                                this.#controlledValue = value;
                                this.#valueChangeNotifier.notify(this);
                            }));
                        }
                    }
                });
            },
            onRemoved: (pointer) => {
                this.#controlSource.proxy.onControlSourceRemove(mapPointerToControlSource(pointer.pointerType));
                pointer.box.accept({
                    visitTrackBox: (box) => {
                        assert(this.#trackBoxAdapter.unwrapOrNull()?.address?.equals(box.address) === true, `Unknown ${box}`);
                        this.#trackBoxAdapter = Option.None;
                        if (this.#context.isMainThread) {
                            this.#automationHandle.ifSome(handle => handle.terminate());
                            this.#automationHandle = Option.None;
                            this.#controlledValue = null;
                            this.#valueChangeNotifier.notify(this);
                        }
                    }
                });
            }
        }, ...ExternalControlTypes));
        /*
        For debugging: It's not live because floating errors expose false positives,
            and I am too lazy to implement this in the mappings itself.
        */
        if (field.getValue() !== valueMapping.clamp(field.getValue())) {
            console.warn(`${name} (${field.getValue()}) is out of bounds`, valueMapping);
        }
    }
    registerMidiControl() {
        this.#controlSource.proxy.onControlSourceAdd("midi");
        this.#midiControlled = true;
        return {
            terminate: () => {
                this.#midiControlled = false;
                this.#controlSource.proxy.onControlSourceRemove("midi");
            }
        };
    }
    get field() { return this.#field; }
    get valueMapping() { return this.#valueMapping; }
    get stringMapping() { return this.#stringMapping; }
    get name() { return this.#name; }
    get anchor() { return this.#anchor; }
    get type() { return this.#field.type; }
    get address() { return this.#field.address; }
    get track() { return this.#trackBoxAdapter; }
    valueAt(position) {
        const optTrack = this.#trackBoxAdapter;
        if (optTrack.nonEmpty()) {
            const track = optTrack.unwrap();
            if (track.enabled) {
                return this.valueMapping.y(track.valueAt(position, this.getUnitValue()));
            }
        }
        return this.getValue();
    }
    subscribe(observer) { return this.#valueChangeNotifier.subscribe(observer); }
    catchupAndSubscribe(observer) {
        observer(this);
        return this.subscribe(observer);
    }
    catchupAndSubscribeControlSources(observer) {
        if (this.#midiControlled) {
            observer.onControlSourceAdd("midi");
        }
        this.#field.pointerHub.filter(...ExternalControlTypes)
            .forEach(pointer => observer.onControlSourceAdd(mapPointerToControlSource(pointer.pointerType)));
        return this.#controlSource.subscribe(observer);
    }
    getValue() { return this.#field.getValue(); }
    setValue(value) { this.#field.setValue(value); }
    setUnitValue(value) { this.setValue(this.#valueMapping.y(value)); }
    getUnitValue() { return this.#valueMapping.x(this.getValue()); }
    getControlledValue() { return this.#valueMapping.y(this.getControlledUnitValue()); }
    getControlledUnitValue() { return this.#controlledValue ?? this.getUnitValue(); }
    getControlledPrintValue() { return this.#stringMapping.x(this.getControlledValue()); }
    getPrintValue() { return this.#stringMapping.x(this.getValue()); }
    setPrintValue(text) {
        const result = this.#stringMapping.y(text);
        if (result.type === "unitValue") {
            this.setUnitValue(clamp(result.value, 0.0, 1.0));
        }
        else if (result.type === "explicit") {
            this.setValue(this.valueMapping.clamp(result.value));
        }
        else {
            console.debug(`Unknown text input: '${result.value}'`);
        }
    }
    reset() { this.setValue(this.#valueMapping.clamp(this.#field.initValue)); }
    terminate() {
        this.#automationHandle.ifSome(handle => handle.terminate());
        this.#automationHandle = Option.None;
        this.#terminator.terminate();
    }
}
const mapPointerToControlSource = (pointer) => {
    switch (pointer) {
        case Pointers.Automation:
            return "automated";
        case Pointers.Modulation:
            return "modulated";
        case Pointers.MidiControl:
            return "midi";
        case Pointers.ParameterController:
            return "external";
        default:
            return panic(`${pointer.toString()} is an unknown pointer type`);
    }
};
